import { Constants } from './Constants.js';
import { RotationUtil } from './RotationHandler.js';

interface JournalMapping {
  [key: string]: string;
}

export class JournalOperations {
  public getCropping(brand: string, journals: string[]): JournalMapping {
    if (brand === Constants.BRAND_SFLY) {
      return this.convertSnftoSflyCropping(journals);
    } else if (brand === Constants.BRAND_SNF) {
      return this.convertSflytoSnfCropping(journals);
    }
    throw new Error('Unsupported brand');
  }

  public getRotations(brand: string, journals: string[]): JournalMapping {
    if (brand === Constants.BRAND_SFLY) {
      return this.convertSnftoSflyRotation(journals);
    } else if (brand === Constants.BRAND_SNF) {
      return this.convertSflytoSnfRotation(journals);
    }
    throw new Error('Unsupported brand');
  }

  public getFilters(brand: string, journals: string[]): JournalMapping {
    if (brand === Constants.BRAND_SFLY) {
      return this.convertSnftoSflyFilters(journals);
    } else if (brand === Constants.BRAND_SNF) {
      return this.convertSflytoSnfFilters(journals);
    }
    throw new Error('Unsupported brand');
  }

  private convertSnftoSflyFilters(journals: string[]): JournalMapping {
    const mappedObject: JournalMapping = {};

    for (const journal of journals) {
      switch (journal) {
        case Constants.SNF_EFFECT_NONE:
          mappedObject[journal] = Constants.SFLY_EFFECT_NONE;
          break;
        case Constants.SNF_EFFECT_AUTO:
          mappedObject[journal] = Constants.SFLY_EFFECT_AUTO;
          break;
        case Constants.SNF_EFFECT_VINTAGE:
          mappedObject[journal] = Constants.SFLY_EFFECT_VINTAGE;
          break;
        case Constants.SNF_EFFECT_AFS_SEPIA:
          mappedObject[journal] = Constants.SFLY_EFFECT_AFS_SEPIA;
          break;
        case Constants.SNF_EFFECT_SUNSHINE:
          mappedObject[journal] = Constants.SFLY_EFFECT_SUNSHINE;
          break;
        case Constants.SNF_EFFECT_BLACKANDWHITE:
          mappedObject[journal] = Constants.SFLY_EFFECT_BLACKANDWHITE;
          break;
        case Constants.SNF_EFFECT_VIBRANT:
          mappedObject[journal] = Constants.SFLY_EFFECT_VIBRANT;
          break;
        case Constants.SNF_ENHANCE:
          mappedObject[journal] = Constants.SFLY_ENHANCE;
          break;
        default:
          if (journal.indexOf(Constants.SNF_EFFECT_SATURATE) === 0) {
            const saValue = journal.split(':')[1];
            mappedObject[journal] = Constants.SFLY_EFFECT_SATURATE + saValue;
          } else if (journal.indexOf(Constants.SNF_EFFECT_SOFTEN) === 0) {
            const soValue = journal.split(':')[1];
            mappedObject[journal] = Constants.SFLY_EFFECT_SOFTEN + soValue;
          } else if (journal.indexOf(Constants.SNF_EFFECT_SEPIA) === 0) {
            const spValue = journal.split(':')[1];
            mappedObject[journal] = Constants.SFLY_EFFECT_SEPIA + spValue;
          } else if (journal.indexOf(Constants.SFLY_RED_EYE) === 0) {
            mappedObject[journal] = journal;
          } else if (journal.startsWith('ef=')) {
            const sflyValue = journal.substring(3);
            const sflyEffectMap: { [key: string]: string } = {
              none: 'NONE',
              auto: 'AUTO',
              'afs-vintage': 'AFS-Vintage',
              afs_vintage: 'AFS_VINTAGE',
              'afs-sepia': 'AFS-Sepia',
              afs_sepia: 'AFS_SEPIA',
              'afs-sunshine': 'AFS-Sunshine',
              afs_sunshine: 'AFS_SUNSHINE',
              'afs-blackwhite': 'AFS-BlackWhite',
              monochrome: 'MONOCHROME',
              'afs-vibrant': 'AFS-Vibrant',
              afs_vibrant: 'AFS_VIBRANT',
              saturate: 'SATURATE',
              soften: 'SOFTEN',
              sepia: 'SEPIA',
            };
            const sflyValueLower = sflyValue.toLowerCase();
            if (sflyEffectMap[sflyValueLower]) {
              mappedObject[journal] = 'effect=' + sflyEffectMap[sflyValueLower];
            } else if (
              sflyValueLower.startsWith('saturate:') ||
              sflyValueLower.startsWith('soften:') ||
              sflyValueLower.startsWith('sepia:')
            ) {
              const colonIdx = sflyValue.indexOf(':');
              const canonicalPrefix = sflyValue
                .substring(0, colonIdx)
                .toUpperCase();
              const intensity = sflyValue.substring(colonIdx + 1);
              mappedObject[journal] =
                'effect=' + canonicalPrefix + ':' + intensity;
            }
          }
          break;
      }
    }
    return mappedObject;
  }

  private convertSflytoSnfFilters(journals: string[]): JournalMapping {
    const mappedObject: JournalMapping = {};

    for (const journal of journals) {
      switch (journal) {
        case Constants.SFLY_EFFECT_NONE:
          mappedObject[journal] = Constants.SNF_EFFECT_NONE;
          break;
        case Constants.SFLY_EFFECT_AUTO:
          mappedObject[journal] = Constants.SNF_EFFECT_AUTO;
          break;
        case Constants.SFLY_EFFECT_VINTAGE:
          mappedObject[journal] = Constants.SNF_EFFECT_VINTAGE;
          break;
        case Constants.SFLY_EFFECT_AFS_VINTAGE:
          mappedObject[journal] = Constants.SNF_EFFECT_VINTAGE;
          break;
        case Constants.SFLY_EFFECT_AFS_SEPIA:
          mappedObject[journal] = Constants.SNF_EFFECT_AFS_SEPIA;
          break;
        case Constants.SFLY_EFFECT_AFS_SEPIA_US:
          mappedObject[journal] = Constants.SNF_EFFECT_AFS_SEPIA;
          break;
        case Constants.SFLY_EFFECT_SUNSHINE:
          mappedObject[journal] = Constants.SNF_EFFECT_SUNSHINE;
          break;
        case Constants.SFLY_EFFECT_AFS_SUNSHINE:
          mappedObject[journal] = Constants.SNF_EFFECT_SUNSHINE;
          break;
        case Constants.SFLY_EFFECT_BLACKANDWHITE:
        case Constants.SFLY_EFFECT_MONOCHROME:
          mappedObject[journal] = Constants.SNF_EFFECT_BLACKANDWHITE;
          break;
        case Constants.SFLY_EFFECT_VIBRANT:
          mappedObject[journal] = Constants.SNF_EFFECT_VIBRANT;
          break;
        case Constants.SFLY_EFFECT_AFS_VIBRANT:
          mappedObject[journal] = Constants.SNF_EFFECT_VIBRANT;
          break;
        case Constants.SFLY_ENHANCE:
          mappedObject[journal] = Constants.SNF_ENHANCE;
          break;
        default:
          if (journal.indexOf(Constants.SFLY_EFFECT_SATURATE) === 0) {
            const saValue = journal.split(':')[1];
            mappedObject[journal] = Constants.SNF_EFFECT_SATURATE + saValue;
          } else if (journal.indexOf(Constants.SFLY_EFFECT_SOFTEN) === 0) {
            const soValue = journal.split(':')[1];
            mappedObject[journal] = Constants.SNF_EFFECT_SOFTEN + soValue;
          } else if (journal.indexOf(Constants.SFLY_EFFECT_SEPIA) === 0) {
            const spValue = journal.split(':')[1];
            mappedObject[journal] = Constants.SNF_EFFECT_SEPIA + spValue;
          } else if (journal.indexOf(Constants.SFLY_RED_EYE) === 0) {
            mappedObject[journal] = journal;
          }
          break;
      }
    }
    return mappedObject;
  }

  private convertSnftoSflyRotation(journals: string[]): JournalMapping {
    const mappedObject: JournalMapping = {};

    for (const journal of journals) {
      switch (true) {
        case journal.includes('rf='):
          let rotationValue = RotationUtil.reduceJournal(journal);

          switch (rotationValue) {
            case Constants.SNF_ROTATION_90:
              mappedObject[journal] = Constants.SFLY_ROTATION_90;
              mappedObject[rotationValue] = Constants.SFLY_ROTATION_90;
              break;
            case Constants.SNF_ROTATION_180:
              mappedObject[journal] = Constants.SFLY_ROTATION_180;
              mappedObject[rotationValue] = Constants.SFLY_ROTATION_180;
              break;
            case Constants.SNF_ROTATION_270:
              mappedObject[journal] = Constants.SFLY_ROTATION_270;
              mappedObject[rotationValue] = Constants.SFLY_ROTATION_270;
              break;
            default:
              mappedObject[journal] = '';
              mappedObject[rotationValue] = '';
              break;
          }
          break;
      }
    }
    return mappedObject;
  }

  private convertSflytoSnfRotation(journals: string[]): JournalMapping {
    const mappedObject: JournalMapping = {};

    for (const journal of journals) {
      switch (journal) {
        case Constants.SFLY_ROTATION_0:
          mappedObject[journal] = '';
          break;
        case Constants.SFLY_ROTATION_90:
          mappedObject[journal] = Constants.SNF_ROTATION_90;
          break;
        case Constants.SFLY_ROTATION_180:
          mappedObject[journal] = Constants.SNF_ROTATION_180;
          break;
        case Constants.SFLY_ROTATION_270:
          mappedObject[journal] = Constants.SNF_ROTATION_270;
          break;
      }
    }
    return mappedObject;
  }

  private convertSnftoSflyCropping(journals: string[]): JournalMapping {
    const mappedObject: JournalMapping = {};

    for (const journal of journals) {
      const rotationValue = this.getRotationFromJournal(journals);
      if (journal.indexOf(Constants.SNF_CROP) === 0) {
        const croppingParams = journal.split('=')[1].split(',');
        if (croppingParams.length === 4) {
          let x1 = parseFloat(croppingParams[1]);
          let y1 = 1 - parseFloat(croppingParams[2]);
          let x2 = parseFloat(croppingParams[3]);
          let y2 = 1 - parseFloat(croppingParams[0]);

          let cropll = [x1, y1];
          let cropur = [x2, y2];

          if (
            rotationValue === 90 ||
            rotationValue === 180 ||
            rotationValue === 270
          ) {
            const rotatedCrop = this.rotateShutterflyCrop(
              cropll,
              cropur,
              rotationValue,
            );
            cropll = rotatedCrop.cropll;
            cropur = rotatedCrop.cropur;
            x1 = cropll[0];
            y1 = cropll[1];
            x2 = cropur[0];
            y2 = cropur[1];
          }

          const crop =
            Constants.SFLY_CROP_LL +
            x1 +
            ',' +
            y1 +
            '&' +
            Constants.SFLY_CROP_UR +
            x2 +
            ',' +
            y2;
          mappedObject[journal] = crop;
        }
      }
    }
    return mappedObject;
  }

  private convertSflytoSnfCropping(journals: string[]): JournalMapping {
    const mappedObject: JournalMapping = {};

    for (const journal of journals) {
      if (journal.indexOf(Constants.SFLY_CROP_LL) === 0) {
        const cropllParams = journal
          .split(Constants.SFLY_CROP_LL)[1]
          .split('&')[0]
          .split(',');
        const cropurParams = journal
          .split('&' + Constants.SFLY_CROP_UR)[1]
          .split(',');
        if (cropllParams.length === 2 && cropurParams.length === 2) {
          const a1 = cropllParams[0];
          const b1 = (1 - parseFloat(cropurParams[1])).toFixed(2);
          const a2 = cropurParams[0];
          const b2 = (1 - parseFloat(cropllParams[1])).toFixed(2);
          const crop = Constants.SNF_CROP + b1 + ',' + a1 + ',' + b2 + ',' + a2;
          mappedObject[journal] = crop;
        }
      }
    }
    return mappedObject;
  }

  private rotateShutterflyCrop(
    cropll: number[],
    cropur: number[],
    angle: number,
  ) {
    const x1 = cropll[0],
      y1 = cropll[1];
    const x2 = cropur[0],
      y2 = cropur[1];

    let points = [
      [x1, y1],
      [x2, y2],
    ];

    // Apply rotation transformation
    let rotated = points.map(([x, y]) => {
      if (angle === 90) {
        return [y, 1 - x]; // 90° clockwise
      } else if (angle === 180) {
        return [1 - x, 1 - y];
      } else if (angle === 270) {
        return [1 - y, x]; // 270° clockwise (or 90° counter-clockwise)
      } else {
        throw new Error('Only 90 or 270 degrees supported.');
      }
    });

    const xs = rotated.map((p) => p[0]);
    const ys = rotated.map((p) => p[1]);

    const llx = Math.min(...xs);
    const lly = Math.min(...ys);
    const urx = Math.max(...xs);
    const ury = Math.max(...ys);

    return {
      cropll: [llx, lly],
      cropur: [urx, ury],
    };
  }

  private getRotationFromJournal(journals: string[]): number {
    let rotation = 0;
    for (const journal of journals) {
      if (journal.includes('rf=')) {
        const rotationValue = RotationUtil.reduceJournal(journal);

        switch (rotationValue) {
          case Constants.SNF_ROTATION_90:
            rotation = 90;
            break;
          case Constants.SNF_ROTATION_180:
            rotation = 180;
            break;
          case Constants.SNF_ROTATION_270:
            rotation = 270;
            break;
          default:
            rotation = 0;
            break;
        }
      }
    }
    return rotation;
  }
}
